import { useState, forwardRef } from 'react';
import OrderReceiptDropdown from './OrderReceiptDropdown';

const OrderFormStickyBar = forwardRef(({
  formId,
  grandTotal,
  totalAdvance,
  balance,
  loading,
  submitDisabled = false,
  submitLabel,
  submitClassName,
  lastCreatedOrderReceipt,
  minAdvanceRequired,
  isAdvanceSufficient,
  showAdvancePolicy = false,
  onClearForm,
  clearLabel = 'New order',
  onViewBucket,
  bucketItemCount = 0,
  viewBucketLabel = 'See all orders',
  viewBucketShortLabel = 'Bucket',
  variant = 'default',
  showAdvanceInput = false,
  advanceValue,
  onAdvanceChange,
  onAdvanceFull,
  onAdvanceClear,
  advanceInputDisabled = false,
  showOrderTotals = true,
  showSettledInput = false,
  settledValue,
  onSettledChange,
  onSettledClear,
  catalogGrandTotal,
}, ref) => {
  const [advanceMenuOpen, setAdvanceMenuOpen] = useState(false);
  const formatMoney = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN')}`;
  const isInCard = variant === 'inCard';
  const catalogTotal = catalogGrandTotal !== undefined ? catalogGrandTotal : grandTotal;

  const stickyRootClass = isInCard
    ? 'order-form-sticky--in-card order-form-sticky--in-card-overlay'
    : 'order-form-sticky';

  const mobileFooterBtn = 'order-footer-action-btn admin-btn';

  const desktopViewBucketButton = onViewBucket ? (
    <button
      type="button"
      onClick={onViewBucket}
      disabled={bucketItemCount === 0}
      className="order-action-btn admin-btn-neutral disabled:opacity-50"
    >
      {viewBucketLabel} ({bucketItemCount})
    </button>
  ) : null;

  const submitButton = (className = '', compact = false) => (
    <button
      type="submit"
      {...(formId ? { form: formId } : {})}
      disabled={loading || submitDisabled}
      className={`${submitClassName} ${compact ? 'h-9 min-h-0' : 'min-h-11 md:min-h-0'} ${className}`}
    >
      {loading ? 'Creating…' : submitLabel}
    </button>
  );

  const advanceInputField = (className = '') => (
    <input
      type="number"
      value={advanceValue ?? ''}
      onChange={(e) => onAdvanceChange?.(parseFloat(e.target.value) || 0)}
      disabled={advanceInputDisabled}
      className={`toolbar-input h-8 text-sm text-center px-1 max-w-full focus:ring-green-500 focus:border-green-500 disabled:opacity-50 ${className}`}
      min="0"
      step="1"
      placeholder="0"
      aria-label="Advance amount"
    />
  );

  const settledInputField = (className = '') => (
    <input
      type="number"
      value={settledValue === '' || settledValue === undefined || settledValue === null ? '' : settledValue}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '') {
          onSettledChange?.('');
          return;
        }
        onSettledChange?.(parseFloat(raw) || 0);
      }}
      className={`toolbar-input h-8 text-sm text-center px-1 max-w-full focus:ring-green-500 focus:border-green-500 ${className}`}
      min="0"
      step="1"
      placeholder="Optional"
      aria-label="Agreed price"
    />
  );

  const advanceShortcutButtons = (compact = false) =>
    !advanceInputDisabled && onAdvanceFull && onAdvanceClear ? (
      <>
        <button
          type="button"
          onClick={onAdvanceFull}
          className={`${mobileFooterBtn} admin-btn-success px-1.5 text-[10px] shrink-0`}
        >
          {compact ? 'Full' : 'Full amount'}
        </button>
        <button
          type="button"
          onClick={onAdvanceClear}
          className={`${mobileFooterBtn} admin-btn-neutral px-1.5 text-[10px] shrink-0`}
        >
          Clear
        </button>
      </>
    ) : null;

  const settledClearButton = onSettledClear ? (
    <button
      type="button"
      onClick={onSettledClear}
      className={`${mobileFooterBtn} admin-btn-neutral px-1.5 text-[10px] shrink-0`}
    >
      Clear
    </button>
  ) : null;

  const mobileAdvanceMenu = !advanceInputDisabled && onAdvanceFull && onAdvanceClear ? (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => setAdvanceMenuOpen((open) => !open)}
        className={`${mobileFooterBtn} admin-btn-neutral w-7 px-0 text-sm`}
        aria-label="Advance shortcuts"
        aria-expanded={advanceMenuOpen}
      >
        ⋯
      </button>
      {advanceMenuOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-20"
            aria-label="Close advance menu"
            onClick={() => setAdvanceMenuOpen(false)}
          />
          <div className="absolute bottom-full right-0 mb-1 z-30 min-w-[7rem] rounded-md border border-gray-200 bg-white shadow-lg py-1">
            <button
              type="button"
              className="receipt-menu-item"
              onClick={() => {
                onAdvanceFull();
                setAdvanceMenuOpen(false);
              }}
            >
              Full amount
            </button>
            <button
              type="button"
              className="receipt-menu-item"
              onClick={() => {
                onAdvanceClear();
                setAdvanceMenuOpen(false);
              }}
            >
              Clear
            </button>
          </div>
        </>
      )}
    </div>
  ) : null;

  const summaryRow = (
    <div className="order-row-summary text-sm flex flex-wrap items-center gap-x-2 gap-y-1">
      {showSettledInput ? (
        <>
          <span>
            <span className="text-gray-500">Original price</span>{' '}
            <strong>{formatMoney(catalogTotal)}</strong>
          </span>
          <span className="text-gray-300 hidden sm:inline">·</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="text-gray-500">Agreed</span>
            {settledInputField('w-20 sm:w-24')}
            {settledClearButton}
          </span>
        </>
      ) : (
        <span>
          <span className="text-gray-500">Total</span> <strong>{formatMoney(grandTotal)}</strong>
        </span>
      )}
      <span className="text-gray-300 hidden sm:inline">·</span>
      {showAdvanceInput ? (
        <span className="inline-flex items-center gap-1.5">
          <span className="text-gray-500">Advance</span>
          {advanceInputField('w-20 sm:w-24')}
          {advanceShortcutButtons()}
        </span>
      ) : (
        <span>
          <span className="text-gray-500">Advance</span> <strong>{formatMoney(totalAdvance)}</strong>
        </span>
      )}
      <span className="text-gray-300 hidden sm:inline">·</span>
      <span className={balance === 0 ? 'text-green-700' : 'text-red-700'}>
        <span className="text-gray-500">Balance</span> <strong>{formatMoney(balance)}</strong>
      </span>
      {showAdvancePolicy && (
        <>
          <span className="text-gray-300">·</span>
          <span className={isAdvanceSufficient ? 'text-green-700' : 'text-red-700'}>
            Min 40%: {formatMoney(minAdvanceRequired)}
            {isAdvanceSufficient ? ' OK' : ' short'}
          </span>
        </>
      )}
    </div>
  );

  const actionButtons = (submitClass = 'w-auto') => (
    <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto shrink-0">
      <OrderReceiptDropdown lastCreatedOrderReceipt={lastCreatedOrderReceipt} />
      {onClearForm && (
        <button
          type="button"
          onClick={onClearForm}
          className="order-action-btn admin-btn-neutral"
        >
          {clearLabel}
        </button>
      )}
      {desktopViewBucketButton}
      {submitButton(submitClass)}
    </div>
  );

  const mobileInlineSummary = (
    <div className="text-xs leading-snug flex flex-wrap items-center gap-x-1 gap-y-0.5">
      {showSettledInput ? (
        <>
          <span>
            <span className="text-gray-500">Orig</span>{' '}
            <strong className="text-gray-900">{formatMoney(catalogTotal)}</strong>
          </span>
          <span className="text-gray-300">·</span>
          <span className="inline-flex flex-wrap items-center gap-1">
            <span className="text-gray-500">Agreed</span>
            {settledInputField('w-14 h-7 text-xs !pl-1 !pr-1')}
          </span>
        </>
      ) : (
        <span>
          <span className="text-gray-500">Total</span>{' '}
          <strong className="text-gray-900">{formatMoney(grandTotal)}</strong>
        </span>
      )}
      <span className="text-gray-300">·</span>
      <span className="inline-flex flex-wrap items-center gap-1">
        <span className="text-gray-500">Adv</span>
        {showAdvanceInput ? (
          <>
            {advanceInputField('w-14 h-7 text-xs !pl-1 !pr-1')}
            {mobileAdvanceMenu}
          </>
        ) : (
          <strong className="text-gray-900">{formatMoney(totalAdvance)}</strong>
        )}
      </span>
      <span className="text-gray-300">·</span>
      <span className={balance === 0 ? 'text-green-700' : 'text-red-700'}>
        <span className="text-gray-500">Bal</span>{' '}
        <strong>{formatMoney(balance)}</strong>
      </span>
      {showAdvancePolicy && !isAdvanceSufficient && (
        <>
          <span className="text-gray-300">·</span>
          <span className="text-red-700 font-medium">Min 40% short</span>
        </>
      )}
    </div>
  );

  const mobileCompactSideActions = (
    <>
      <OrderReceiptDropdown lastCreatedOrderReceipt={lastCreatedOrderReceipt} />
      {onClearForm && (
        <button
          type="button"
          onClick={onClearForm}
          className={`${mobileFooterBtn} admin-btn-neutral shrink-0`}
        >
          {clearLabel}
        </button>
      )}
      {onViewBucket && (
        <button
          type="button"
          onClick={onViewBucket}
          disabled={bucketItemCount === 0}
          className={`${mobileFooterBtn} admin-btn-neutral shrink-0 disabled:opacity-50`}
        >
          {viewBucketShortLabel} ({bucketItemCount})
        </button>
      )}
    </>
  );

  const mobileCompactActionStrip = onViewBucket ? (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 min-w-0">{mobileCompactSideActions}</div>
      {submitButton('w-full', true)}
    </div>
  ) : (
    <div className="flex items-center gap-1.5 min-w-0">
      {mobileCompactSideActions}
      {submitButton('flex-1 min-w-0 text-xs px-2 text-center leading-snug', true)}
    </div>
  );

  const mobileAdvanceCell = showAdvanceInput ? (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">Advance</div>
      {advanceInputField()}
    </div>
  ) : (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">Advance</div>
      <div className="text-sm font-semibold text-gray-900">{formatMoney(totalAdvance)}</div>
    </div>
  );

  const mobileViewBucketButton = onViewBucket ? (
    <button
      type="button"
      onClick={onViewBucket}
      disabled={bucketItemCount === 0}
      className="order-action-btn admin-btn-neutral w-full disabled:opacity-50"
    >
      {viewBucketShortLabel} ({bucketItemCount})
    </button>
  ) : null;

  if (isInCard) {
    return (
      <div ref={ref} className={stickyRootClass}>
        {/* Mobile: compact floating footer — inline summary + single action strip */}
        <div className="md:hidden space-y-1 pt-1.5 px-0.5">
          {showOrderTotals && mobileInlineSummary}
          {mobileCompactActionStrip}
        </div>

        {/* Desktop: horizontal in-card layout aligned with form card */}
        <div className={`hidden md:flex w-full items-center gap-4 pt-3 ${showOrderTotals ? 'justify-between' : 'justify-end'}`}>
          {showOrderTotals && summaryRow}
          {actionButtons('w-auto')}
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className={stickyRootClass}>
      {/* Mobile: compact grid layout, inline at end of form */}
      <div className="md:hidden space-y-2.5 pt-4">
        {showOrderTotals && (
          <>
            {showSettledInput ? (
              <div className="grid grid-cols-2 gap-2 text-center">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Original</div>
                  <div className="text-sm font-semibold text-gray-900">{formatMoney(catalogTotal)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Agreed</div>
                  {settledInputField('w-full')}
                </div>
                {mobileAdvanceCell}
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Balance</div>
                  <div className={`text-sm font-semibold ${balance === 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {formatMoney(balance)}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Total</div>
                  <div className="text-sm font-semibold text-gray-900">{formatMoney(grandTotal)}</div>
                </div>
                {mobileAdvanceCell}
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Balance</div>
                  <div className={`text-sm font-semibold ${balance === 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {formatMoney(balance)}
                  </div>
                </div>
              </div>
            )}

            {showAdvanceInput && advanceShortcutButtons(true)}
            {showSettledInput && onSettledClear && (
              <button
                type="button"
                onClick={onSettledClear}
                className={`${mobileFooterBtn} admin-btn-neutral w-full text-xs`}
              >
                Clear agreed price
              </button>
            )}

            {showAdvancePolicy && (
              <p className={`text-xs text-center ${isAdvanceSufficient ? 'text-green-700' : 'text-red-700'}`}>
                Min 40%: {formatMoney(minAdvanceRequired)}
                {isAdvanceSufficient ? ' · OK' : ' · short'}
              </p>
            )}
          </>
        )}

        <div className="flex items-center gap-2">
          <OrderReceiptDropdown lastCreatedOrderReceipt={lastCreatedOrderReceipt} />
          {onClearForm && (
            <button
              type="button"
              onClick={onClearForm}
              className="order-action-btn admin-btn-neutral flex-1"
            >
              {clearLabel}
            </button>
          )}
        </div>

        {onViewBucket ? (
          <div className="grid grid-cols-2 gap-2">
            {mobileViewBucketButton}
            {submitButton('w-full')}
          </div>
        ) : (
          submitButton('w-full')
        )}
      </div>

      {/* Desktop: fixed bar layout */}
      <div className={`hidden md:flex max-w-6xl mx-auto flex-col sm:flex-row sm:items-center gap-3 ${showOrderTotals ? 'sm:justify-between' : 'sm:justify-end'}`}>
        {showOrderTotals && summaryRow}
        {actionButtons('w-auto')}
      </div>
    </div>
  );
});

OrderFormStickyBar.displayName = 'OrderFormStickyBar';

export default OrderFormStickyBar;
